import React, { useState, useEffect, useCallback } from 'react';
import { getNotifications, getUnreadNotificationCount, markNotificationRead, markAllNotificationsRead } from '../services/api';
import { useLang } from '../context/LanguageContext';
import { useRideEvents } from '../hooks/useRideEvents';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const { t } = useLang();
  const n = t.notifications;

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await getUnreadNotificationCount();
      setUnreadCount(res.data.count);
    } catch {
      // silently ignore - the bell just won't update this tick
    }
  }, []);

  useEffect(() => { fetchUnreadCount(); }, [fetchUnreadCount]);
  useRideEvents(useCallback(async () => {
    await fetchUnreadCount();
    setLoaded(false);
  }, [fetchUnreadCount]));

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  const handleToggle = async (e) => {
    e.stopPropagation();
    const next = !open;
    setOpen(next);
    if (next && !loaded) {
      try {
        const res = await getNotifications(0);
        setNotifications(res.data.content);
        setLoaded(true);
      } catch {
        // dropdown will just show empty state
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setUnreadCount(0);
      setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
    } catch {
      // best-effort - badge stays as-is until next refresh
    }
  };

  const handleItemClick = async (item) => {
    if (item.read) return;
    try {
      await markNotificationRead(item.id);
      setNotifications((prev) => prev.map((x) => (x.id === item.id ? { ...x, read: true } : x)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      // best-effort
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleToggle}
        className="relative p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
        aria-label={n.title}
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 bg-red-500 text-white text-[10px] font-bold min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-2xl shadow-lg py-2 z-50 max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between px-4 py-1.5">
            <span className="text-sm font-bold text-slate-900">{n.title}</span>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllRead} className="text-xs text-primary-600 hover:text-primary-700 font-semibold">
                {n.markAllRead}
              </button>
            )}
          </div>
          {notifications.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">{n.empty}</p>
          ) : (
            notifications.map((item) => (
              <button
                key={item.id}
                onClick={() => handleItemClick(item)}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex flex-col gap-0.5 hover:bg-slate-50 ${
                  item.read ? 'text-slate-500' : 'text-slate-900 font-medium bg-primary-50/50'
                }`}
              >
                <span>{n.types[item.type] || item.type}</span>
                {item.fromLocation && (
                  <span className="text-xs text-slate-400 truncate">{item.fromLocation} → {item.toLocation}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
